import React, { useState, useEffect, useRef } from 'react';
import { auth } from '../firebase/config';
import toast from 'react-hot-toast';
import { Mic, MicOff, Volume2, VolumeX, Send, BookOpen, Clock, Download, X as CloseIcon, StickyNote, Target, Plus, Trash2, FileDown, CheckCircle, XCircle, AlertTriangle, TrendingUp } from 'lucide-react';
import jsPDF from 'jspdf';
import SmartPaymentModal from '../components/payment/SmartPaymentModal';
import aiEducatorService from '../services/aiEducatorService';

const FUNCTIONS_URL = 'https://us-central1-mahstry.cloudfunctions.net';

const AIEducator = () => {
  // Add custom styles
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes bounce-subtle {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-3px); }
      }
      .animate-bounce-subtle {
        animation: bounce-subtle 2s infinite;
      }
      .ai-highlight {
        transition: all 0.2s ease-in-out;
      }
      .ai-highlight:hover {
        transform: scale(1.02);
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const [step, setStep] = useState('setup');
  const [subject, setSubject] = useState('');
  const [chapter, setChapter] = useState('');
  const [topic, setTopic] = useState('');
  const [examClass, setExamClass] = useState('');
  const [language, setLanguage] = useState('english'); // 'english', 'hindi', 'hinglish'
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(true);
  const [isListening, setIsListening] = useState(false);
  const [chapterOutline, setChapterOutline] = useState([]);
  const [currentTopic, setCurrentTopic] = useState(0);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showEndChatModal, setShowEndChatModal] = useState(false);
  const [dailyUsage, setDailyUsage] = useState(0);
  
  // Subscription feature
  const [showPlanModal, setShowPlanModal] = useState(false); // Don't show by default
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'yearly'
  const [userSubscription, setUserSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [usageStats, setUsageStats] = useState(null);
  
  // Notes feature
  const [showNotesPanel, setShowNotesPanel] = useState(false);
  const [notes, setNotes] = useState([]);
  const [currentNote, setCurrentNote] = useState('');
  const [selectedText, setSelectedText] = useState('');
  
  // Practice questions feature
  const [showPracticePanel, setShowPracticePanel] = useState(false);
  const [practiceQuestions, setPracticeQuestions] = useState([]);
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [scores, setScores] = useState({});
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [speakingMessageIndex, setSpeakingMessageIndex] = useState(null);
  const [highlightedNote, setHighlightedNote] = useState('');
  
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const synthRef = useRef(window.speechSynthesis);
  const timerIntervalRef = useRef(null);

  // Plans configuration
  const PLANS = [
    {
      id: 'trial',
      name: 'Samosa Pack',
      monthlyPrice: 0,
      yearlyPrice: 0,
      hours: 3,
      validity: '3 days',
      icon: '🥟',
      color: 'from-green-400 to-emerald-500',
      tagline: 'Just a taste!',
      funLine: 'Free chai pe charcha with AI! ☕',
      features: ['3 hours of learning', 'All features unlocked', 'No credit card needed'],
      cta: 'Try Samosa Pack',
      isTrial: true
    },
    {
      id: 'student',
      name: 'Chai Lover',
      monthlyPrice: 149,
      yearlyPrice: 1341, // 9 months price (save 3 months)
      hours: 20,
      yearlyHours: 240,
      validity: '30 days',
      icon: '☕',
      color: 'from-blue-400 to-cyan-500',
      tagline: 'Daily dose of learning',
      funLine: 'Less than 1 movie ticket! 🎬',
      perDay: '~40 mins/day',
      costPerHour: '₹7.45/hour',
      features: ['20 hours per month', 'All subjects covered', 'Practice questions', 'Notes & PDF export'],
      cta: 'Get Chai Lover',
      yearlySavings: 447 // 3 months savings
    },
    {
      id: 'basic',
      name: 'Biryani Box',
      monthlyPrice: 249,
      yearlyPrice: 2241, // 9 months price (save 3 months)
      hours: 40,
      yearlyHours: 480,
      validity: '30 days',
      icon: '🍛',
      color: 'from-purple-400 to-pink-500',
      tagline: 'Full meal deal!',
      funLine: '₹8/day - Less than a samosa! 🥟',
      perDay: '~1.3 hours/day',
      costPerHour: '₹6.22/hour',
      badge: 'BESTSELLER',
      recommended: true,
      features: ['40 hours per month', 'All subjects covered', 'Notes & PDF export', 'Unlimited practice questions'],
      cta: 'Order Biryani Box',
      yearlySavings: 747 // 3 months savings
    },
    {
      id: 'intensive',
      name: 'Buffet Bonanza',
      monthlyPrice: 799,
      yearlyPrice: 7191, // 9 months price (save 3 months)
      hours: 150,
      yearlyHours: 1800,
      validity: '30 days',
      icon: '🍽️',
      color: 'from-orange-400 to-red-500',
      tagline: 'All you can learn!',
      funLine: '100x cheaper than coaching! 💯',
      perDay: '~5 hours/day',
      costPerHour: '₹5.32/hour',
      features: ['150 hours per month', 'All subjects covered', 'Unlimited everything'],
      cta: 'Go Buffet Mode',
      yearlySavings: 2397 // 3 months savings
    }
  ];

  const calculatePrice = (plan) => {
    if (plan.isTrial) return { base: 0, gst: 0, total: 0 };
    
    const basePrice = billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
    const gst = basePrice * 0.18; // Keep precise decimal
    const total = basePrice + gst;
    
    return { 
      base: basePrice, 
      gst: gst.toFixed(2), // Precise GST with 2 decimals
      total: total.toFixed(2) // Precise total with 2 decimals
    };
  };

  const handlePlanSelect = async (plan) => {
    setSelectedPlan(plan);
    if (plan.id === 'trial') {
      // Save trial subscription to Firestore
      if (auth.currentUser) {
        const planData = {
          planId: plan.id,
          planName: plan.name,
          billingCycle: 'trial',
          hours: plan.hours
        };
        
        const paymentInfo = {
          paymentId: `trial_${Date.now()}`,
          amount: 0
        };
        
        const result = await aiEducatorService.createSubscription(
          auth.currentUser.uid,
          planData,
          paymentInfo
        );
        
        if (result.success) {
          setShowPlanModal(false);
          toast.success('🎉 Samosa Pack activated! Enjoy your free learning snack!');
          // Reload subscription to update UI
          await checkUserSubscription();
        } else {
          toast.error('Failed to activate trial. Please try again.');
        }
      } else {
        toast.error('Please login to activate the trial.');
      }
    } else {
      setShowPaymentModal(true);
    }
  };

  const handlePayment = () => {
    // This function is no longer needed - using SubscriptionPaymentModal
  };

  const handlePaymentSuccess = async (response, transactionData) => {
    console.log('Payment successful:', response, transactionData);
    
    // Create subscription in Firestore
    const planData = {
      planId: selectedPlan.id,
      planName: selectedPlan.name,
      billingCycle: billingCycle,
      hours: billingCycle === 'monthly' ? selectedPlan.hours : selectedPlan.yearlyHours
    };
    
    const paymentInfo = {
      paymentId: transactionData.paymentId,
      amount: transactionData.amount
    };
    
    const result = await aiEducatorService.createSubscription(
      auth.currentUser.uid,
      planData,
      paymentInfo
    );
    
    if (result.success) {
      setShowPaymentModal(false);
      setShowPlanModal(false);
      
      const messages = {
        student: '☕ Chai Lover activated! Your daily learning dose is ready!',
        basic: '🍛 Biryani Box delivered! Full meal of knowledge unlocked!',
        intensive: '🍽️ Buffet Bonanza activated! All-you-can-learn mode ON!'
      };
      
      toast.success(messages[selectedPlan.id] || `🎉 ${selectedPlan.name} activated! Happy learning!`);
      
      // Reload subscription
      await checkUserSubscription();
    } else {
      toast.error('Failed to activate subscription. Please contact support.');
    }
  };

  const handlePaymentFailure = (error) => {
    console.error('Payment failed:', error);
    toast.error('Payment failed. Please try again.');
    setShowPaymentModal(false);
  };

  useEffect(() => {
    // Check subscription on component mount
    checkUserSubscription();
    
    // Initialize speech recognition
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      
      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInput(transcript);
        setIsListening(false);
      };
      
      recognitionRef.current.onerror = () => {
        setIsListening(false);
        toast.error('Speech recognition error');
      };
    }

    // Mobile speech synthesis warmup - unlock TTS on first user interaction
    const unlockSpeechSynthesis = () => {
      const utterance = new SpeechSynthesisUtterance('');
      utterance.volume = 0;
      synthRef.current.speak(utterance);
      // Remove listeners after first interaction
      document.removeEventListener('touchstart', unlockSpeechSynthesis);
      document.removeEventListener('click', unlockSpeechSynthesis);
    };
    
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      document.addEventListener('touchstart', unlockSpeechSynthesis, { once: true });
      document.addEventListener('click', unlockSpeechSynthesis, { once: true });
    }

    // Load daily usage from localStorage
    loadDailyUsage();
    
    return () => {
      document.removeEventListener('touchstart', unlockSpeechSynthesis);
      document.removeEventListener('click', unlockSpeechSynthesis);
    };
  }, []);

  // Set up global handler for highlight clicks
  useEffect(() => {
    window.addHighlightToNotes = (text) => {
      if (!text) return;
      const newNote = {
        id: Date.now(),
        content: `Key Point: ${text}`,
        highlightedText: text,
        timestamp: new Date().toISOString()
      };
      setNotes(prev => [...prev, newNote]);
      toast.success('Key point added to notes!');
      setShowNotesPanel(true);
    };
    
    return () => {
      delete window.addHighlightToNotes;
    };
  }, []);

  const checkUserSubscription = async () => {
    if (!auth.currentUser) {
      setSubscriptionLoading(false);
      setShowPlanModal(true);
      return;
    }

    setSubscriptionLoading(true);
    const result = await aiEducatorService.checkSubscriptionValidity(auth.currentUser.uid);
    
    if (result.isValid) {
      setUserSubscription(result.subscription);
      setShowPlanModal(false);
      // Load usage stats
      const stats = await aiEducatorService.getUsageStats(auth.currentUser.uid);
      if (stats.success) {
        setUsageStats(stats.stats);
      }
    } else {
      setUserSubscription(null);
      setShowPlanModal(true);
      
      if (result.reason === 'hours_exhausted') {
        toast.error(result.message);
        setShowUpgradeModal(true);
      } else if (result.reason === 'expired') {
        toast.error(result.message);
      }
    }
    
    setSubscriptionLoading(false);
  };

  const loadDailyUsage = () => {
    const today = new Date().toDateString();
    const stored = localStorage.getItem('aiEducatorUsage');
    
    if (stored) {
      const data = JSON.parse(stored);
      if (data.date === today) {
        setDailyUsage(data.seconds || 0);
      } else {
        // New day, reset
        localStorage.setItem('aiEducatorUsage', JSON.stringify({ date: today, seconds: 0 }));
        setDailyUsage(0);
      }
    } else {
      localStorage.setItem('aiEducatorUsage', JSON.stringify({ date: today, seconds: 0 }));
      setDailyUsage(0);
    }
  };

  const updateDailyUsage = (seconds) => {
    const today = new Date().toDateString();
    const newTotal = dailyUsage + seconds;
    setDailyUsage(newTotal);
    localStorage.setItem('aiEducatorUsage', JSON.stringify({ date: today, seconds: newTotal }));
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    
    // Trigger MathJax typeset whenever messages change
    if (window.MathJax) {
      setTimeout(() => {
        window.MathJax.typesetPromise().catch((err) => console.log('MathJax typeset failed: ', err));
      }, 500);
    }
  }, [messages]);

  // Timer effect
  useEffect(() => {
    if (step === 'learning' && sessionStartTime) {
      timerIntervalRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - sessionStartTime) / 1000));
      }, 1000);

      return () => {
        if (timerIntervalRef.current) {
          clearInterval(timerIntervalRef.current);
        }
      };
    }
  }, [step, sessionStartTime]);

  const speak = (text, messageIndex = null) => {
    if (!autoSpeak) return;
    
    // Remove markdown, LaTeX, and Emojis before speaking
    const cleanText = text
      // Remove LaTeX math environments $ ... $ and $$ ... $$
      .replace(/\$\$([\s\S]*?)\$\$/g, '$1')
      .replace(/\$([\s\S]*?)\$/g, '$1')
      // Remove LaTeX math environments \[ ... \] and \( ... \)
      .replace(/\\\[([\s\S]*?)\\\]/g, '$1')
      .replace(/\\\(([\s\S]*?)\\\)/g, '$1')
      // Remove specific LaTeX commands
      .replace(/\\frac\{([^{}]*)\}\{([^{}]*)\}/g, '$1 divided by $2')
      .replace(/\\times/g, 'times')
      .replace(/\\cdot/g, 'times')
      .replace(/\\text\{([^{}]*)\}/g, '$1')
      .replace(/\\[a-zA-Z]+/g, '')
      .replace(/[\\{}[\]]/g, ' ')
      // Remove Emojis (Regex for common emoji ranges)
      .replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu, '')
      // Remove <highlight> tags for TTS
      .replace(/<highlight>/gi, '')
      .replace(/<\/highlight>/gi, '')
      // Remove markdown symbols
      .replace(/[#*`_~]/g, '')
      .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1')
      .replace(/^\s*[-•+]\s+/gm, '')
      .replace(/\n+/g, '. ')
      .replace(/\.\.+/g, '.')
      .replace(/\s+/g, ' ')
      .trim();
    
    synthRef.current.cancel();
    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.rate = 0.95;
    utterance.pitch = 1.05;
    utterance.onstart = () => {
      setIsSpeaking(true);
      setSpeakingMessageIndex(messageIndex);
    };
    utterance.onend = () => {
      setIsSpeaking(false);
      setSpeakingMessageIndex(null);
    };
    
    // Add highlighting logic using onboundary
    utterance.onboundary = (event) => {
      if (event.name === 'word') {
        // Track current word if needed, but for notes, we want full sentences or key points
        // Let's at least track the progress
        const currentProgress = event.charIndex;
        // Optionally update progress state
      }
    };
    
    // Mobile browsers require user gesture to start speech
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    // Voices selection logic
    const voices = synthRef.current.getVoices();
    if (voices.length > 0) {
      if (language === 'hindi' || language === 'hinglish') {
        // Try to find a Hindi voice first, then fall back to English
        utterance.voice = voices.find(v => v.lang.startsWith('hi')) || 
                          voices.find(v => v.lang.startsWith('en')) || 
                          voices[0];
      } else {
        // English voice
        utterance.voice = voices.find(v => v.lang.startsWith('en')) || voices[0];
      }
    }
    
    synthRef.current.speak(utterance);
  };

  const stopSpeaking = () => {
    synthRef.current.cancel();
    setIsSpeaking(false);
  };

  const toggleListening = () => {
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      recognitionRef.current?.start();
      setIsListening(true);
    }
  };

  const startLesson = async () => {
    if (!subject || !examClass) {
      toast.error('Please fill in subject and exam/class');
      return;
    }

    setStep('learning');
    setSessionStartTime(Date.now());
    const initialMessage = {
      role: 'user',
      content: `I want to learn ${subject}${chapter ? ` - ${chapter}` : ''}${topic ? ` (specifically ${topic})` : ''} for ${examClass}. Please start teaching me.`
    };
    
    setMessages([initialMessage]);
    await sendMessage(initialMessage, true);
  };

  const sendMessage = async (messageObj = null, isInitial = false) => {
    const msgToSend = messageObj || { role: 'user', content: input };
    
    if (!isInitial && !input.trim() && !messageObj) return;

    setIsLoading(true);
    if (!isInitial && !messageObj) {
      setMessages(prev => [...prev, msgToSend]);
      setInput('');
    }

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }
      const token = await user.getIdToken();

      // Language specific instructions
      const languageInstruction = language === 'hindi' 
        ? "Speak and teach entirely in HINDI. Use Devanagari script for Hindi text. Keep technical terms in English if necessary but explain them in Hindi."
        : language === 'hinglish'
        ? "Speak and teach in HINGLISH (a mix of Hindi and English). Use Latin script (English letters) for the Hindi parts. This should sound like a natural conversation between Indian friends. E.g., 'Aaj hum Newton's Laws ke baare mein baat karenge.'"
        : "Speak and teach entirely in ENGLISH.";

      // Build enhanced system prompt with structured teaching
      const systemPrompt = `You are a warm, engaging, and world-class classroom teacher with PhD-level expertise in ${subject}. You are teaching ${chapter ? `${chapter}` : subject}${topic ? ` (${topic})` : ''} for ${examClass || 'students'}.

🌍 LANGUAGE REQUIREMENT:
${languageInstruction}

🍎 CLASSROOM TEACHING STYLE:
- Use student's name if known, and address them warmly.
- Use conversational phrases like "Let's dive into...", "Imagine this...", "Does that make sense so far?", "That's a great question!", "Let me put it this way...".
- PAUSE occasionally (mentally) by asking the student for confirmation or their thoughts.
- RELATE concepts to everyday life to make them memorable.
- ENCOURAGE the student constantly. Use positive reinforcement ("Excellent!", "You're getting it!", "Keep going!").
- USE VOCAL VARIETY: While text-based, use punctuation and emojis to simulate a dynamic speaking tone.

📚 HIGHLIGHTING & NOTES:
- CRITICAL: You MUST wrap the ENTIRE definition, law, formula, or key concept in <highlight>...</highlight> tags. 
- Do NOT just highlight a single word. Highlight the full sentence or multiple lines if they represent a complete, meaningful unit of knowledge.
- Highlights should be "Note-worthy": if a student saves it, it should make sense as a standalone note.
- Use <highlight> for: 
  * Full definitions: "<highlight>Photosynthesis is the process by which green plants and some other organisms use sunlight to synthesize foods with the help of chlorophyll.</highlight>"
  * Mathematical Laws: "<highlight>The Pythagorean theorem states that in a right-angled triangle, the square of the hypotenuse is equal to the sum of the squares of the other two sides: a² + b² = c².</highlight>"
  * Historical Facts: "<highlight>The French Revolution began in 1789 with the Storming of the Bastille, marking a major turning point in modern European history.</highlight>"

🔢 MATHEMATICAL EXPRESSIONS:
- CRITICAL: Use standard LaTeX for ALL math formulas.
- Use DOUBLE dollar signs for block formulas: $$ \text{formula} $$
- Use SINGLE dollar signs for inline formulas: $ \text{formula} $
- Example: "The sine ratio is defined as: $$ \sin(\theta) = \frac{\text{Opposite}}{\text{Hypotenuse}} $$"
- Ensure every backslash (\) is preserved. Wrap complex formulas in <highlight> tags so students can save them.

🎨 STYLE GUIDELINES:
- Be enthusiastic! Use emojis in your text (e.g., 🌟, 💡, 📝), but keep them outside of the <highlight> tags.
- Mix facts with engagement.
- Keep paragraphs short (2-3 lines max).
- Use bullet points for clarity.
- Make complex ideas simple with CORRECT analogies.

🎓 YOUR EXPERTISE:
- You have 20+ years of teaching experience
- You are known for making complex concepts crystal clear
- You only provide ACCURATE, VERIFIED information
- If you're unsure about something, you say "Let me clarify that" and provide the correct information
- You cite principles, laws, and established facts when relevant

⚠️ ACCURACY FIRST:
- NEVER guess or make up information
- Only teach established, verified concepts
- If a student asks something outside your expertise, acknowledge it
- Correct any misconceptions immediately
- Use precise terminology and definitions

📚 TEACHING STRUCTURE:
1. **Introduction** (First message only):
   - Start with a fascinating, ACCURATE fact or real-world application.
   - Create a COMPREHENSIVE numbered outline of main concepts (format: "1. **Concept Name**").
   - ADJUST SYLLABUS DEPTH: For high-level exams like ${examClass}, provide a detailed and advanced syllabus (8-10 subtopics if needed). For basic classes, keep it simpler (4-6 topics).
   - Set clear learning objectives for this level.
   - Build excitement with real examples!

2. **Progressive Teaching** (Cover ALL topics systematically):
   - Teach ONE concept at a time with PRECISION.
   - For exams like ${examClass}, include advanced derivations, complex problem-solving techniques, and exam-specific shortcuts.
   - Use: Real examples, proven analogies, established facts.
   - After each concept: Ask 1 quick check question.
   - Based on answer: Give feedback, correct if needed, then MOVE TO NEXT CONCEPT.
   - Don't deep-dive unless student asks "explain more" or "in detail".

3. **Engagement Techniques**:
   - Use real-world applications and case studies.
   - Share verified facts and historical context.
   - Use emojis to make it visual and fun.
   - Keep explanations crisp (3-4 lines per concept).
   - Use proven analogies that students can relate to.

4. **Question Strategy**:
   - After teaching a concept: Ask ONE simple, clear question.
   - After student answers: 
     * Give brief, accurate feedback (1-2 lines).
     * Correct misconceptions with the right information.
     * Then IMMEDIATELY move to NEXT concept.
   - Don't get stuck on one topic!

5. **Progress Tracking**:
   - Keep mental count of concepts covered.
   - After covering all main concepts: "Great! We've covered [list topics]. Ready for a quick recap?".
   - End with accurate summary and encouragement.

🎨 STYLE GUIDELINES:
- Be enthusiastic but ACCURATE! 
- Use conversational, friendly tone.
- Mix facts with engagement.
- Keep paragraphs short (2-3 lines max).
- Use bullet points for clarity.
- Add emojis for visual appeal.
- Make complex ideas simple with CORRECT analogies.

⚠️ CRITICAL RULES:
- ACCURACY over everything - never compromise on facts.
- NEVER deep-dive into one topic unless asked.
- ALWAYS progress through the chapter systematically.
- Keep moving forward after each Q&A.
- Cover breadth first, depth only if requested.
- If you make an error, correct it immediately.

Remember: You are a TOP-TIER educator. Your reputation is built on ACCURACY and CLARITY. Teach the ENTIRE chapter systematically with verified, correct information for the ${examClass} level!`;

      const apiMessages = [
        { role: 'system', content: systemPrompt },
        ...(isInitial ? [msgToSend] : [...messages, msgToSend])
      ];

      const response = await fetch(`${FUNCTIONS_URL}/openaiChatCompletion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: apiMessages,
          temperature: 0.8,
          max_tokens: 1200
        })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to get response');
      }

      const assistantMessage = {
        role: 'assistant',
        content: result.response
      };

      setMessages(prev => {
        const newMessages = [...prev, assistantMessage];
        
        // Extract outline from first message
        if (isInitial && result.response) {
          const outline = extractOutline(result.response);
          if (outline.length > 0) {
            setChapterOutline(outline);
          }
        }
        
        speak(result.response, newMessages.length - 1);
        return newMessages;
      });
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Failed to get response. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatMessage = (content) => {
    if (!content) return '';
    
    // First, split content to handle LaTeX separately
    // We want to avoid escaping backslashes inside LaTeX delimiters
    const parts = content.split(/(\$\$[\s\S]*?\$\$|\$[\s\S]*?\$)/g);
    
    const formattedParts = parts.map(part => {
      // If it's a LaTeX part, return it as is (MathJax will handle it)
      if (part.startsWith('$')) {
        return part;
      }
      
      // Otherwise, apply standard formatting
      return part
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        // Restore our special tags and make them interactive
        .replace(/&lt;highlight&gt;([\s\S]*?)&lt;\/highlight&gt;/gi, (match, p1) => {
          const cleanText = p1.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
          const sanitizedForJS = cleanText.replace(/'/g, "\\'").replace(/\n/g, ' ').trim();
          return `<div class="ai-highlight bg-yellow-100 border-l-4 border-yellow-500 p-4 rounded-r-lg cursor-pointer hover:bg-yellow-200 transition-all group relative my-4 shadow-sm" onclick="window.addHighlightToNotes('${sanitizedForJS}')">
            <div class="font-medium text-yellow-900 leading-relaxed text-base">${cleanText}</div>
            <div class="hidden group-hover:inline-flex absolute -top-4 -right-4 items-center gap-1.5 text-xs bg-indigo-600 text-white px-3 py-1.5 rounded-full shadow-lg z-10 whitespace-nowrap">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
              Save as Study Note
            </div>
          </div>`;
        })
        // Headers (more robust)
        .replace(/^(#{1,3})\s+(.*?)$/gm, (match, hashes, title) => {
          const level = hashes.length;
          const classes = level === 1 ? 'text-2xl font-black text-indigo-950 mt-8 mb-4' : 
                          level === 2 ? 'text-xl font-bold text-indigo-900 mt-6 mb-3' : 
                                        'text-lg font-bold text-indigo-800 mt-4 mb-2';
          return `<h${level} class="${classes}">${title}</h${level}>`;
        })
        // Specific headers like "Key Points:" or "Example:"
        .replace(/^(Key Points|Graphical Representation|Example|Quick Check Question):/gm, '<h3 class="text-lg font-bold text-indigo-800 mt-6 mb-3 underline decoration-indigo-200 decoration-2 underline-offset-4">$1:</h3>')
        // Separators
        .replace(/\n#\n/g, '<hr class="my-8 border-t-2 border-indigo-50 rounded-full" />')
        .replace(/^#\s*$/gm, '<hr class="my-8 border-t-2 border-indigo-50 rounded-full" />')
        // Bold
        .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-gray-900">$1</strong>')
        // Italic
        .replace(/\*(.*?)\*/g, '<em class="italic text-gray-600">$1</em>')
        // Code
        .replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-pink-600 border border-gray-200">$1</code>')
        // Bullet points
        .replace(/^\s*-\s+(.*?)$/gm, '<li class="ml-4 list-disc mb-1">$1</li>')
        // Line breaks
        .replace(/\n/g, '<br/>');
    });
    
    return formattedParts.join('');
  };

  const extractOutline = (content) => {
    const lines = content.split('\n');
    const outline = [];
    lines.forEach(line => {
      if (line.match(/^\d+\.\s+\*\*(.*?)\*\*/)) {
        const match = line.match(/^\d+\.\s+\*\*(.*?)\*\*/);
        outline.push({ title: match[1], completed: false });
      }
    });
    return outline;
  };

  const jumpToTopic = async (topicIndex) => {
    if (isLoading) return; // Prevent double jumping
    
    const topicTitle = chapterOutline[topicIndex]?.title;
    if (!topicTitle) return;
    
    setCurrentTopic(topicIndex);
    
    // Send message to AI asking about this topic
    const message = { role: 'user', content: `Please teach me about ${topicTitle} in detail` };
    setMessages(prev => [...prev, message]);
    setInput('');
    
    try {
      // Use the updated messages list (including the new user message)
      await sendMessage(message, false);
    } catch (error) {
      console.error('Error jumping to topic:', error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage();
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs}h ${mins}m ${secs}s`;
    } else if (mins > 0) {
      return `${mins}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const handleEndChat = () => {
    setShowEndChatModal(true);
  };

  const confirmEndChat = async () => {
    stopSpeaking();
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
    
    // Update daily usage
    updateDailyUsage(elapsedTime);
    
    // Track usage in subscription
    if (auth.currentUser && userSubscription) {
      await aiEducatorService.trackUsage(auth.currentUser.uid, elapsedTime);
      // Reload usage stats
      const stats = await aiEducatorService.getUsageStats(auth.currentUser.uid);
      if (stats.success) {
        setUsageStats(stats.stats);
      }
    }
    
    toast.success(`Session ended! You studied for ${formatTime(elapsedTime)}`);
    
    // Reset to setup
    setStep('setup');
    setMessages([]);
    setChapterOutline([]);
    setCurrentTopic(0);
    setSessionStartTime(null);
    setElapsedTime(0);
    setShowEndChatModal(false);
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      const maxWidth = pageWidth - (margin * 2);
      let yPosition = margin;

      // Title
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text(`${subject}${chapter ? ` - ${chapter}` : ''}`, margin, yPosition);
      yPosition += 10;

      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      doc.text(`${examClass} | Duration: ${formatTime(elapsedTime)}`, margin, yPosition);
      yPosition += 15;

      // Content
      doc.setFontSize(11);
      
      messages.filter(m => m.role === 'assistant').forEach((message) => {
        // Clean markdown from content
        const cleanContent = message.content
          .replace(/[#*`_~]/g, '')
          .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1');

        const lines = doc.splitTextToSize(cleanContent, maxWidth);
        
        lines.forEach(line => {
          if (yPosition > pageHeight - margin) {
            doc.addPage();
            yPosition = margin;
          }
          doc.text(line, margin, yPosition);
          yPosition += 7;
        });
        
        yPosition += 5; // Space between messages
      });

      // Save
      const fileName = `${subject}${chapter ? `_${chapter}` : ''}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    }
  };

  // Notes Functions
  const addNote = () => {
    if (!currentNote.trim()) return;
    
    const newNote = {
      id: Date.now(),
      content: currentNote,
      highlightedText: selectedText,
      timestamp: new Date().toISOString()
    };
    
    setNotes(prev => [...prev, newNote]);
    setCurrentNote('');
    setSelectedText('');
    toast.success('Note added!');
  };

  const deleteNote = (noteId) => {
    setNotes(prev => prev.filter(note => note.id !== noteId));
    toast.success('Note deleted');
  };

  const exportNotes = () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      const maxWidth = pageWidth - (margin * 2);
      let yPosition = margin;

      // Title
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('My Study Notes', margin, yPosition);
      yPosition += 10;

      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      doc.text(`${subject}${chapter ? ` - ${chapter}` : ''}`, margin, yPosition);
      yPosition += 8;
      doc.text(`${examClass} | ${new Date().toLocaleDateString()}`, margin, yPosition);
      yPosition += 15;

      // Notes
      doc.setFontSize(11);
      
      notes.forEach((note, index) => {
        // Note number
        doc.setFont(undefined, 'bold');
        const noteTitle = `Note ${index + 1}`;
        doc.text(noteTitle, margin, yPosition);
        yPosition += 7;
        
        doc.setFont(undefined, 'normal');
        
        // Highlighted text if exists
        if (note.highlightedText) {
          doc.setFont(undefined, 'italic');
          const highlightLines = doc.splitTextToSize(`"${note.highlightedText}"`, maxWidth);
          highlightLines.forEach(line => {
            if (yPosition > pageHeight - margin) {
              doc.addPage();
              yPosition = margin;
            }
            doc.text(line, margin, yPosition);
            yPosition += 6;
          });
          yPosition += 3;
          doc.setFont(undefined, 'normal');
        }
        
        // Note content
        const contentLines = doc.splitTextToSize(note.content, maxWidth);
        contentLines.forEach(line => {
          if (yPosition > pageHeight - margin) {
            doc.addPage();
            yPosition = margin;
          }
          doc.text(line, margin, yPosition);
          yPosition += 6;
        });
        
        yPosition += 8; // Space between notes
      });

      const fileName = `Notes_${subject}${chapter ? `_${chapter}` : ''}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      toast.success('Notes exported successfully!');
    } catch (error) {
      console.error('Error exporting notes:', error);
      toast.error('Failed to export notes');
    }
  };

  const handleTextSelection = () => {
    const selection = window.getSelection();
    const text = selection.toString().trim();
    if (text) {
      setSelectedText(text);
      setShowNotesPanel(true);
      toast.success('Text highlighted! Add a note about it.');
    }
  };

  // Practice Questions Functions
  const generatePracticeQuestions = async (difficulty = 'medium') => {
    setIsGeneratingQuestions(true);
    setShowPracticePanel(true);
    
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }
      const token = await user.getIdToken();

      // Get the last few assistant messages for context
      const recentContent = messages
        .filter(m => m.role === 'assistant')
        .slice(-3)
        .map(m => m.content)
        .join('\n\n');

      const prompt = `Based on the following lesson content about ${subject}${chapter ? ` - ${chapter}` : ''}, generate 5 practice questions at ${difficulty} difficulty level.

Lesson Content:
${recentContent}

Generate questions in the following JSON format:
[
  {
    "type": "mcq",
    "question": "Question text here?",
    "options": ["Option A", "Option B", "Option C", "Option D"],
    "correctAnswer": 0,
    "explanation": "Why this is the correct answer"
  },
  {
    "type": "short",
    "question": "Question text here?",
    "correctAnswer": "Provide a comprehensive and clear predefined answer here. This will be used to evaluate student answers.",
    "explanation": "Detailed explanation of why this answer is correct"
  }
]

Mix of question types: 3 multiple choice (mcq) and 2 short answer (short).
Make questions test understanding, not just memorization.
Ensure questions are clear and unambiguous.
The 'correctAnswer' for 'short' type should be a thorough, ideal response.`;

      const response = await fetch(`${FUNCTIONS_URL}/openaiChatCompletion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: 'You are an expert educator creating practice questions. Always respond with valid JSON only.' },
            { role: 'user', content: prompt }
          ],
          temperature: 0.7,
          max_tokens: 1500
        })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to generate questions');
      }

      // Parse the JSON response
      const questionsText = result.response.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const questions = JSON.parse(questionsText);
      
      setPracticeQuestions(questions);
      // Cache questions in localStorage
      localStorage.setItem('mahstry_practice_questions', JSON.stringify(questions));
      
      setCurrentQuestionIndex(0);
      setUserAnswers({});
      setShowResults(false);
      setScores({});
      toast.success(`Generated ${questions.length} practice questions!`);
    } catch (error) {
      console.error('Error generating questions:', error);
      toast.error('Failed to generate questions. Please try again.');
    } finally {
      setIsGeneratingQuestions(false);
    }
  };

  const submitAnswer = (questionIndex, answer) => {
    setUserAnswers(prev => ({
      ...prev,
      [questionIndex]: answer
    }));
  };

  const normalizeAnswer = (answer) => {
    if (!answer) return '';
    return answer
      .toLowerCase()
      .trim()
      .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, '') // Remove punctuation
      .replace(/\s+/g, ' ') // Normalize spaces
      .replace(/\band\b/g, ',') // Convert 'and' to comma for list comparison
      .split(/[,;]/) // Split by comma or semicolon
      .map(s => s.trim())
      .filter(s => s.length > 0)
      .sort()
      .join(',');
  };

  const checkShortAnswer = (userAnswer, correctAnswer) => {
    const normalizedUser = normalizeAnswer(userAnswer);
    const normalizedCorrect = normalizeAnswer(correctAnswer);
    
    // Check if normalized answers match
    if (normalizedUser === normalizedCorrect) {
      return true;
    }
    
    // Check if user answer contains all key terms from correct answer
    const userTerms = normalizedUser.split(',');
    const correctTerms = normalizedCorrect.split(',');
    
    // If all correct terms are present in user answer, consider it correct
    const allTermsPresent = correctTerms.every(term => 
      userTerms.some(userTerm => 
        userTerm.includes(term) || term.includes(userTerm)
      )
    );
    
    return allTermsPresent;
  };

  const checkAnswers = async () => {
    setIsEvaluating(true);
    setShowResults(true);
    
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');
      const token = await user.getIdToken();
      
      const evaluationData = practiceQuestions.map((q, idx) => ({
        question: q.question,
        type: q.type,
        userAnswer: userAnswers[idx] !== undefined ? (q.type === 'mcq' ? q.options[userAnswers[idx]] : userAnswers[idx]) : "No answer provided",
        predefinedAnswer: q.type === 'mcq' ? q.options[q.correctAnswer] : q.correctAnswer,
        options: q.options || null
      }));

      const prompt = `Evaluate the following student answers for a set of practice questions.
For each question, compare the student's answer with the predefined correct answer.

SCORING RULES:
- For objective questions (type: 'mcq'), the score MUST be exactly 0 or 1.
- For subjective questions (type: 'short'), the score can be a decimal between 0 and 1 (e.g., 0.5, 0.75, 1.0) based on accuracy, completeness, and clarity compared to the predefined answer.
- If the student's answer is missing or completely irrelevant, the score should be 0.

Questions and Answers:
${JSON.stringify(evaluationData, null, 2)}

Respond with a JSON array of scores in the same order as the questions:
[
  { "score": 0.8, "feedback": "Brief feedback about the answer" },
  ...
]`;

      const response = await fetch(`${FUNCTIONS_URL}/openaiChatCompletion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: 'You are an expert educator evaluating student answers. Always respond with valid JSON only.' },
            { role: 'user', content: prompt }
          ],
          temperature: 0.3,
          max_tokens: 1000
        })
      });

      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      // Parse the JSON response
      const evaluationText = result.response.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const evaluations = JSON.parse(evaluationText);
      
      const newScores = {};
      evaluations.forEach((evalItem, idx) => {
        newScores[idx] = evalItem;
      });
      setScores(newScores);
      
      const totalScore = evaluations.reduce((acc, curr) => acc + curr.score, 0);
      toast.success(`Total Score: ${totalScore.toFixed(1)} / ${practiceQuestions.length}`);

    } catch (error) {
      console.error('Error evaluating answers:', error);
      toast.error('Failed to evaluate answers. Using basic check.');
      
      // Fallback to basic check
      const fallbackScores = {};
      practiceQuestions.forEach((q, idx) => {
        const userAnswer = userAnswers[idx];
        let score = 0;
        if (q.type === 'mcq') {
          score = userAnswer === q.correctAnswer ? 1 : 0;
        } else {
          score = checkShortAnswer(userAnswer, q.correctAnswer) ? 1 : 0;
        }
        fallbackScores[idx] = { score, feedback: 'AI evaluation failed, using basic match.' };
      });
      setScores(fallbackScores);
    } finally {
      setIsEvaluating(false);
    }
  };

  // Loading state
  if (subscriptionLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your subscription...</p>
        </div>
      </div>
    );
  }

  if (step === 'setup') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
        {/* Plan Selection Modal */}
        {showPlanModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-y-auto">
              {/* Compact Header */}
              <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-4 rounded-t-2xl text-white text-center relative">
                <h1 className="text-2xl font-bold mb-1">🎓 Mahstry AI Educator</h1>
                <p className="text-sm opacity-90">Your Personal AI Teacher • 24/7 • All Subjects</p>
                
                {/* Monthly/Yearly Toggle */}
                <div className="flex items-center justify-center gap-3 mt-3">
                  <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-white' : 'text-white/60'}`}>
                    Monthly
                  </span>
                  <button
                    onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                    className="relative w-14 h-7 bg-white/30 rounded-full transition-colors"
                  >
                    <div
                      className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                        billingCycle === 'yearly' ? 'transform translate-x-7' : ''
                      }`}
                    />
                  </button>
                  <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-white' : 'text-white/60'}`}>
                    Yearly
                  </span>
                  {billingCycle === 'yearly' && (
                    <span className="text-xs bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-full font-bold animate-pulse">
                      Save 3 months!
                    </span>
                  )}
                </div>
              </div>

              {/* Plans Grid - Compact */}
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                  {PLANS.map((plan) => (
                    <div
                      key={plan.id}
                      className={`relative bg-white rounded-xl border-2 ${
                        plan.recommended ? 'border-purple-500 shadow-lg' : 'border-gray-200'
                      } hover:shadow-xl transition-all duration-300 overflow-hidden`}
                    >
                      {/* Badge */}
                      {plan.badge && (
                        <div className="absolute top-0 right-0 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-bl-lg z-10">
                          ⭐ {plan.badge}
                        </div>
                      )}

                      {/* Compact Icon Header */}
                      <div className={`bg-gradient-to-r ${plan.color} p-3 text-center`}>
                        <div className="text-3xl mb-1">{plan.icon}</div>
                        <h3 className="text-sm font-bold text-white">{plan.name}</h3>
                      </div>

                      {/* Content */}
                      <div className="p-3">
                        {/* Price */}
                        <div className="text-center mb-2">
                          {plan.isTrial ? (
                            <div className="text-2xl font-black text-green-600">FREE</div>
                          ) : (
                            <>
                              <div className="text-2xl font-black text-gray-800">
                                ₹{calculatePrice(plan).base}
                              </div>
                              <div className="text-xs text-gray-500">
                                {billingCycle === 'monthly' ? '/month' : '/year'}
                              </div>
                              {billingCycle === 'yearly' && plan.yearlySavings && (
                                <div className="text-xs text-green-600 font-semibold">
                                  Save ₹{plan.yearlySavings}!
                                </div>
                              )}
                              <div className="text-xs text-gray-400 mt-1">
                                +18% GST applicable
                              </div>
                            </>
                          )}
                        </div>

                        {/* Fun Line - Compact */}
                        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-l-2 border-yellow-400 p-1.5 rounded-r mb-2">
                          <p className="text-xs font-semibold text-gray-700 text-center leading-tight">{plan.funLine}</p>
                        </div>

                        {/* Hours */}
                        <div className="text-center mb-2">
                          <div className="text-sm font-bold text-indigo-600">
                            {billingCycle === 'monthly' ? plan.hours : plan.yearlyHours || plan.hours * 12} hours
                          </div>
                          {plan.perDay && billingCycle === 'monthly' && (
                            <div className="text-xs text-gray-500">{plan.perDay}</div>
                          )}
                        </div>

                        {/* Key Features - All */}
                        <ul className="space-y-1 mb-3">
                          {plan.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-1 text-xs text-gray-700">
                              <span className="text-green-500 flex-shrink-0">✓</span>
                              <span className="leading-tight">{feature}</span>
                            </li>
                          ))}
                        </ul>

                        {/* CTA Button */}
                        <button
                          onClick={() => handlePlanSelect(plan)}
                          className={`w-full py-2 rounded-lg font-bold text-sm text-white transition-all ${
                            plan.recommended
                              ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                              : 'bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-800 hover:to-gray-900'
                          }`}
                        >
                          {plan.cta}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Comparison - Compact */}
                <div className="mt-3 text-center">
                  <p className="text-xs text-gray-600 mb-1">💡 Compare:</p>
                  <div className="flex flex-wrap justify-center gap-2 text-xs">
                    <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Tutor: ₹500-2000/hr</span>
                    <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">Coaching: ₹200-500/hr</span>
                    <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">Mahstry: ₹5-8/hr ✨</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Payment Modal */}
        {showPaymentModal && selectedPlan && (
          <SmartPaymentModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            paymentData={{
              amount: parseFloat(calculatePrice(selectedPlan).total), // Precise amount with decimals
              currency: 'INR',
              userId: auth.currentUser?.uid,
              userName: auth.currentUser?.displayName || 'Student',
              userEmail: auth.currentUser?.email,
              description: `${selectedPlan.name} - ${billingCycle === 'monthly' ? 'Monthly' : 'Yearly'} Subscription`,
              metadata: {
                plan: billingCycle,
                planId: selectedPlan.id,
                planName: selectedPlan.name,
                hours: billingCycle === 'monthly' ? selectedPlan.hours : selectedPlan.yearlyHours,
                basePrice: calculatePrice(selectedPlan).base,
                gst: calculatePrice(selectedPlan).gst,
                totalAmount: calculatePrice(selectedPlan).total,
                productType: 'ai_educator_subscription',
                billingCycle: billingCycle
              }
            }}
            onSuccess={handlePaymentSuccess}
            onFailure={handlePaymentFailure}
            title={`Subscribe to ${selectedPlan.name} ${selectedPlan.icon}`}
          />
        )}

        {/* Upgrade Modal - Hours Exhausted */}
        {showUpgradeModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
              <div className="bg-gradient-to-r from-orange-500 to-red-600 p-6 rounded-t-2xl text-white text-center">
                <AlertTriangle className="w-16 h-16 mx-auto mb-3" />
                <h2 className="text-2xl font-bold">Hours Limit Reached!</h2>
              </div>
              
              <div className="p-6">
                {usageStats && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Current Plan</span>
                      <span className="font-bold text-gray-800">{usageStats.planName}</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Hours Used</span>
                      <span className="font-bold text-red-600">{usageStats.hoursUsed} / {usageStats.hoursLimit}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                      <div className="bg-red-600 h-2 rounded-full" style={{width: '100%'}}></div>
                    </div>
                  </div>
                )}
                
                <p className="text-gray-700 mb-4 text-center">
                  You've used all your learning hours for this month. Upgrade to a higher plan to continue learning!
                </p>
                
                <button
                  onClick={() => {
                    setShowUpgradeModal(false);
                    setShowPlanModal(true);
                  }}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white py-3 rounded-lg font-bold hover:shadow-lg mb-3"
                >
                  Upgrade Plan
                </button>
                
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="w-full border-2 border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-2xl mx-auto">{/* Existing setup form */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <BookOpen className="w-8 h-8 text-indigo-600" />
              <h1 className="text-3xl font-bold text-gray-800">Mahstry AI Educator</h1>
            </div>
            
            <p className="text-gray-600 mb-6">
              Your personal AI teacher that will guide you through any topic with interactive lessons
            </p>

            {/* Warning Box */}
            <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
              <div className="flex items-start gap-3">
                <span className="text-2xl">⚠️</span>
                <div>
                  <h3 className="text-sm font-semibold text-yellow-800 mb-1">Important Notice - AI Limitations</h3>
                  <p className="text-sm text-yellow-700 mb-2">
                    <strong>This AI educator is in learning phase and may occasionally make inaccuracies across all subjects.</strong> 
                    Please be especially cautious with:
                  </p>
                  <ul className="text-sm text-yellow-700 ml-4 list-disc space-y-1">
                    <li><strong>Mathematics:</strong> Complex calculations, multi-step problems, numerical derivations</li>
                    <li><strong>Sciences:</strong> Precise formulas, experimental data, specific numerical values</li>
                    <li><strong>Dates & Facts:</strong> Historical dates, statistical data, specific figures</li>
                    <li><strong>Technical Details:</strong> Exact specifications, precise definitions, advanced topics</li>
                  </ul>
                  <p className="text-sm text-yellow-700 mt-3">
                    <strong>✅ Best for:</strong> Conceptual understanding, explanations, learning frameworks, and general knowledge.
                  </p>
                  <p className="text-sm text-yellow-800 font-semibold mt-2">
                    📚 Always verify important information with your textbooks, teachers, or reliable sources.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject *
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g., Physics, Mathematics, History"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chapter
                </label>
                <input
                  type="text"
                  value={chapter}
                  onChange={(e) => setChapter(e.target.value)}
                  placeholder="e.g., Newton's Laws of Motion"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Specific Topic (Optional)
                </label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g., Third Law of Motion"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Exam/Class *
                </label>
                <input
                  type="text"
                  value={examClass}
                  onChange={(e) => setExamClass(e.target.value)}
                  placeholder="e.g., Class 10 CBSE, JEE Main, NEET"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Language *
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {['english', 'hindi', 'hinglish'].map((lang) => (
                    <button
                      key={lang}
                      onClick={() => setLanguage(lang)}
                      className={`py-3 rounded-lg font-medium capitalize transition-all border-2 ${
                        language === lang
                          ? 'bg-indigo-50 border-indigo-600 text-indigo-700'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-indigo-300'
                      }`}
                    >
                      {lang === 'hinglish' ? 'Hinglish (Mix)' : lang}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={startLesson}
                className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors mt-6"
              >
                Start Learning
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* AI Warning Banner */}
      <div className="fixed top-0 left-0 right-0 bg-yellow-50 border-b border-yellow-200 px-4 py-2 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-sm text-yellow-800">
          <span className="text-lg">⚠️</span>
          <span>
            <strong>Learning Phase:</strong> This AI educator may occasionally make mistakes. 
            Always verify important information with your textbooks or teachers.
          </span>
        </div>
      </div>

      {/* Sidebar - Chapter Outline */}
      {chapterOutline.length > 0 && (
        <div className="w-64 bg-white border-r border-gray-200 p-4 overflow-y-auto mt-12">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Chapter Outline</h3>
          <div className="space-y-2">
            {chapterOutline.map((item, index) => (
              <button
                key={index}
                onClick={() => jumpToTopic(index)}
                className={`w-full text-left p-3 rounded-lg transition-all ${
                  currentTopic === index
                    ? 'bg-indigo-100 text-indigo-700 font-medium'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs flex items-center justify-center mt-0.5">
                    {index + 1}
                  </span>
                  <span className="text-sm">{item.title}</span>
                </div>
              </button>
            ))}
          </div>
          
          {/* Subscription Usage Stats */}
          {usageStats && (
            <div className="mt-6 p-3 bg-purple-50 rounded-lg border-2 border-purple-200">
              <h4 className="text-sm font-semibold text-purple-900 mb-2 flex items-center gap-1">
                <TrendingUp className="w-4 h-4" />
                {usageStats.planName}
              </h4>
              <div className="space-y-2">
                <div>
                  <div className="flex justify-between text-xs text-purple-700 mb-1">
                    <span>Hours Used</span>
                    <span className="font-bold">{usageStats.hoursUsed} / {usageStats.hoursLimit}</span>
                  </div>
                  <div className="w-full bg-purple-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${parseFloat(usageStats.usagePercentage) > 80 ? 'bg-red-500' : 'bg-purple-600'}`}
                      style={{width: `${Math.min(usageStats.usagePercentage, 100)}%`}}
                    ></div>
                  </div>
                </div>
                <p className="text-xs text-purple-700">
                  <span className="font-semibold">{usageStats.hoursRemaining}hrs</span> remaining
                </p>
                <p className="text-xs text-purple-600">
                  {usageStats.daysRemaining} days left
                </p>
                {parseFloat(usageStats.usagePercentage) > 80 && (
                  <button
                    onClick={() => setShowPlanModal(true)}
                    className="w-full text-xs bg-purple-600 text-white py-1 rounded mt-2 hover:bg-purple-700"
                  >
                    Upgrade Plan
                  </button>
                )}
              </div>
            </div>
          )}
          
          {/* Daily Usage Stats */}
          <div className="mt-6 p-3 bg-indigo-50 rounded-lg">
            <h4 className="text-sm font-semibold text-indigo-900 mb-2">Today's Study Time</h4>
            <p className="text-2xl font-bold text-indigo-600">{formatTime(dailyUsage)}</p>
            <p className="text-xs text-indigo-700 mt-1">Keep up the great work! 📚</p>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col mt-12 relative h-[calc(100vh-3rem)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4 shrink-0 shadow-sm z-20">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-gray-800">{subject}</h2>
            <p className="text-sm text-gray-600">
              {chapter && `${chapter} • `}{examClass}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            {/* Timer */}
            <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg">
              <Clock className="w-4 h-4" />
              <span className="text-sm font-medium">{formatTime(elapsedTime)}</span>
            </div>
            
            {/* Notes Button */}
            <button
              onClick={() => setShowNotesPanel(!showNotesPanel)}
              className={`p-2 rounded-lg ${showNotesPanel ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-100 text-gray-600'} hover:bg-yellow-200`}
              title="Notes"
            >
              <StickyNote className="w-5 h-5" />
            </button>
            
            {/* Practice Questions Button */}
            <button
              onClick={() => setShowPracticePanel(!showPracticePanel)}
              className={`p-2 rounded-lg ${showPracticePanel ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-600'} hover:bg-purple-200`}
              title="Practice Questions"
            >
              <Target className="w-5 h-5" />
            </button>
            
            {/* Export PDF */}
            <button
              onClick={exportToPDF}
              className="p-2 rounded-lg bg-green-100 text-green-600 hover:bg-green-200"
              title="Export as PDF"
            >
              <Download className="w-5 h-5" />
            </button>
            
            {/* Audio Controls */}
            <button
              onClick={() => setAutoSpeak(!autoSpeak)}
              className={`p-2 rounded-lg ${autoSpeak ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-600'}`}
            >
              {autoSpeak ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
            {isSpeaking && (
              <button
                onClick={stopSpeaking}
                className="hidden sm:block px-4 py-2 bg-red-100 text-red-600 rounded-lg text-sm font-medium"
              >
                Stop
              </button>
            )}
            
            {/* End Chat */}
            <button
              onClick={handleEndChat}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 flex items-center gap-2"
            >
              <CloseIcon className="w-4 h-4" />
              <span className="hidden md:inline">End Chat</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-row overflow-hidden relative">
        {/* Truly Fixed Side Avatar - Desktop Only */}
        <div className="hidden md:flex flex-col items-center p-8 w-48 lg:w-72 shrink-0 border-r border-gray-100 bg-white overflow-y-auto">
          <div className="sticky top-0 flex flex-col items-center">
            <div className={`relative w-32 h-32 lg:w-56 lg:h-56 rounded-3xl bg-white shadow-2xl border-4 flex items-center justify-center text-6xl lg:text-9xl transition-all duration-500 ${
              isSpeaking ? 'border-indigo-600 scale-105 ring-8 ring-indigo-50 shadow-indigo-200' : 'border-gray-200'
            }`}>
              👨‍🏫
              {isSpeaking && (
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5 bg-indigo-600 px-5 py-2.5 rounded-full shadow-xl ring-4 ring-white z-10">
                  <div className="w-2.5 h-4 bg-white rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                  <div className="w-2.5 h-7 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2.5 h-4 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              )}
            </div>
            <div className="mt-8 bg-indigo-50/50 backdrop-blur-md px-6 py-4 rounded-2xl shadow-sm border border-indigo-100 text-center w-full">
              <p className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em] mb-1">JEE EXPERT</p>
              <p className="text-sm font-bold text-gray-800">AI Educator</p>
              {isSpeaking && (
                <div className="mt-3 flex items-center justify-center gap-2 text-[10px] text-indigo-600 font-bold uppercase animate-pulse">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
                  </span>
                  Teaching Now
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-8 bg-gray-50/30 relative">
          <div className="max-w-4xl mx-auto space-y-8 pb-20">
            {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start gap-3 items-start group'}`}
              onMouseUp={handleTextSelection}
            >
              {message.role === 'assistant' && (
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-2xl shadow-sm border border-indigo-200">
                  👨‍🏫
                </div>
              )}
              <div className={`relative max-w-3xl px-6 py-4 rounded-2xl shadow-sm border transition-all duration-500 ${
                message.role === 'user' 
                  ? 'bg-indigo-600 text-white border-indigo-500' 
                  : `bg-white text-gray-800 border-gray-100 hover:shadow-md ${
                      speakingMessageIndex === index ? 'ring-2 ring-indigo-500 shadow-lg scale-[1.01] bg-indigo-50/30' : ''
                    }`
              }`}>
                <div 
                  className={`prose prose-sm max-w-none ${message.role === 'user' ? 'prose-invert' : ''}`}
                  dangerouslySetInnerHTML={{ __html: formatMessage(message.content) }}
                />
                
                {speakingMessageIndex === index && (
                  <div className="absolute -top-2 -left-2 flex gap-0.5 bg-indigo-600 p-1 rounded-full shadow-lg ring-2 ring-white">
                    <div className="w-1 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                    <div className="w-1 h-3 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-1 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                )}
                
                {message.role === 'assistant' && (
                  /* Message Actions */
                  <div className="absolute -right-12 top-0 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-2">
                    <button 
                      onClick={() => speak(message.content, index)}
                      className={`p-2 bg-white rounded-full shadow-sm border border-gray-100 transition-all ${
                        speakingMessageIndex === index ? 'text-indigo-600 border-indigo-200' : 'text-gray-400 hover:text-indigo-600 hover:border-indigo-200'
                      }`}
                      title="Read aloud"
                    >
                      <Volume2 className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => {
                        const newNote = {
                          id: Date.now(),
                          content: message.content.substring(0, 100) + '...',
                          highlightedText: message.content,
                          timestamp: new Date().toISOString()
                        };
                        setNotes(prev => [...prev, newNote]);
                        toast.success('Message added to notes!');
                        setShowNotesPanel(true);
                      }}
                      className="p-2 bg-white rounded-full shadow-sm border border-gray-100 text-gray-400 hover:text-yellow-600 hover:border-yellow-200 transition-all"
                      title="Add whole message to notes"
                    >
                      <StickyNote className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white px-6 py-4 rounded-2xl shadow-md">
                <div className="flex gap-2">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100" />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200" />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>
    </div>

      {/* Input */}
      <div className="bg-white border-t border-gray-200 p-4 shrink-0">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your answer or question..."
              disabled={isLoading}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            {recognitionRef.current && (
              <button
                type="button"
                onClick={toggleListening}
                className={`p-3 rounded-lg ${
                  isListening ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>
            )}
            <button
              type="submit"
              disabled={isLoading || !input.trim()}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </form>
      </div>
      </div>
      
      {/* Notes Side Panel */}
      {showNotesPanel && (
        <div className="w-80 bg-white border-l border-gray-200 p-4 overflow-y-auto mt-12 flex flex-col">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-yellow-600" />
              My Notes
            </h3>
            <button
              onClick={() => setShowNotesPanel(false)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <CloseIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Selected Text Display */}
          {selectedText && (
            <div className="mb-3 p-2 bg-yellow-50 border-l-4 border-yellow-400 rounded-r text-sm">
              <p className="text-gray-600 italic">"{selectedText}"</p>
            </div>
          )}

          {/* Add Note Input */}
          <div className="mb-4">
            <textarea
              value={currentNote}
              onChange={(e) => setCurrentNote(e.target.value)}
              placeholder="Write your note here..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
              rows="4"
            />
            <button
              onClick={addNote}
              disabled={!currentNote.trim()}
              className="mt-2 w-full px-4 py-2 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Note
            </button>
          </div>

          {/* Notes List */}
          <div className="flex-1 overflow-y-auto space-y-3">
            {notes.length === 0 ? (
              <div className="text-center text-gray-400 py-8">
                <StickyNote className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No notes yet</p>
                <p className="text-xs mt-1">Highlight text and add notes!</p>
              </div>
            ) : (
              notes.map((note) => (
                <div key={note.id} className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                  {note.highlightedText && (
                    <p className="text-xs text-gray-600 italic mb-2 pb-2 border-b border-yellow-200">
                      "{note.highlightedText}"
                    </p>
                  )}
                  <p className="text-sm text-gray-800 mb-2">{note.content}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">
                      {new Date(note.timestamp).toLocaleTimeString()}
                    </span>
                    <button
                      onClick={() => deleteNote(note.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Export Notes Button */}
          {notes.length > 0 && (
            <button
              onClick={exportNotes}
              className="mt-4 w-full px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 flex items-center justify-center gap-2"
            >
              <FileDown className="w-4 h-4" />
              Export Notes as PDF
            </button>
          )}
        </div>
      )}

      {/* Practice Questions Side Panel */}
      {showPracticePanel && (
        <div className="w-96 bg-white border-l border-gray-200 p-4 overflow-y-auto mt-12 flex flex-col">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-600" />
              Practice Questions
            </h3>
            <button
              onClick={() => setShowPracticePanel(false)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <CloseIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Generate Questions Buttons */}
          {practiceQuestions.length === 0 && (
            <div className="space-y-3 mb-4">
              <p className="text-sm text-gray-600 mb-3">
                Generate practice questions based on what you've learned:
              </p>
              <button
                onClick={() => generatePracticeQuestions('easy')}
                disabled={isGeneratingQuestions}
                className="w-full px-4 py-2 bg-green-100 text-green-700 rounded-lg font-medium hover:bg-green-200 disabled:opacity-50"
              >
                Easy Questions
              </button>
              <button
                onClick={() => generatePracticeQuestions('medium')}
                disabled={isGeneratingQuestions}
                className="w-full px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg font-medium hover:bg-yellow-200 disabled:opacity-50"
              >
                Medium Questions
              </button>
              <button
                onClick={() => generatePracticeQuestions('hard')}
                disabled={isGeneratingQuestions}
                className="w-full px-4 py-2 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 disabled:opacity-50"
              >
                Hard Questions
              </button>
              
              {isGeneratingQuestions && (
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <p className="text-sm text-gray-600 mt-2">Generating questions...</p>
                </div>
              )}
            </div>
          )}

          {/* Questions Display */}
          {practiceQuestions.length > 0 && !showResults && (
            <div className="flex-1 overflow-y-auto">
              <div className="mb-4 flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  Question {currentQuestionIndex + 1} of {practiceQuestions.length}
                </span>
                <button
                  onClick={() => {
                    setPracticeQuestions([]);
                    setUserAnswers({});
                    setCurrentQuestionIndex(0);
                  }}
                  className="text-sm text-purple-600 hover:text-purple-700"
                >
                  New Set
                </button>
              </div>

              {practiceQuestions.map((question, qIdx) => (
                <div
                  key={qIdx}
                  className={`mb-6 ${qIdx === currentQuestionIndex ? 'block' : 'hidden'}`}
                >
                  <div className="bg-purple-50 p-4 rounded-lg mb-4">
                    <p className="font-medium text-gray-800 mb-3">{question.question}</p>
                    
                    {question.type === 'mcq' ? (
                      <div className="space-y-2">
                        {question.options.map((option, optIdx) => (
                          <button
                            key={optIdx}
                            onClick={() => submitAnswer(qIdx, optIdx)}
                            className={`w-full text-left px-4 py-2 rounded-lg border-2 transition-all ${
                              userAnswers[qIdx] === optIdx
                                ? 'border-purple-600 bg-purple-100'
                                : 'border-gray-200 bg-white hover:border-purple-300'
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <textarea
                        value={userAnswers[qIdx] || ''}
                        onChange={(e) => submitAnswer(qIdx, e.target.value)}
                        placeholder="Type your answer here..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                        rows="3"
                      />
                    )}
                  </div>

                  <div className="flex gap-2">
                    {currentQuestionIndex > 0 && (
                      <button
                        onClick={() => setCurrentQuestionIndex(currentQuestionIndex - 1)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                      >
                        Previous
                      </button>
                    )}
                    {currentQuestionIndex < practiceQuestions.length - 1 ? (
                      <button
                        onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
                        className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        onClick={checkAnswers}
                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                      >
                        Submit All
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Results Display */}
          {showResults && (
            <div className="flex-1 overflow-y-auto">
              <div className="mb-4">
                <h4 className="text-lg font-bold text-gray-800 mb-2">Results</h4>
                {isEvaluating ? (
                  <div className="bg-purple-50 p-6 rounded-lg text-center animate-pulse">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-2"></div>
                    <p className="text-sm text-purple-600 font-medium">Evaluating your answers...</p>
                  </div>
                ) : (
                  <div className="bg-gradient-to-r from-purple-100 to-indigo-100 p-4 rounded-lg mb-4">
                    <p className="text-2xl font-bold text-purple-700">
                      {Object.values(scores).reduce((acc, curr) => acc + curr.score, 0).toFixed(1)} / {practiceQuestions.length}
                    </p>
                    <p className="text-sm text-purple-600">Total Score</p>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {practiceQuestions.map((question, qIdx) => {
                  const userAnswer = userAnswers[qIdx];
                  const evaluation = scores[qIdx] || { score: 0, feedback: '' };
                  const score = evaluation.score;
                  const isCorrect = score >= 0.8; // High score threshold for green check
                  const isPartial = score > 0 && score < 0.8;

                  return (
                    <div key={qIdx} className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-start gap-2 mb-2">
                        {isCorrect ? (
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        ) : isPartial ? (
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <div className="w-4 h-4 rounded-full border-2 border-yellow-500 border-t-transparent animate-pulse"></div>
                          </div>
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-2">
                            <p className="font-medium text-gray-800 pr-4">{question.question}</p>
                            <span className={`text-xs font-bold px-2 py-1 rounded ${
                              isCorrect ? 'bg-green-100 text-green-700' : 
                              isPartial ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                            }`}>
                              Score: {score}
                            </span>
                          </div>
                          
                          {question.type === 'mcq' ? (
                            <>
                              <p className="text-sm text-gray-600">
                                Your answer: <span className={score === 1 ? 'text-green-600' : 'text-red-600'}>
                                  {question.options[userAnswer] || '(No answer)'}
                                </span>
                              </p>
                              {score < 1 && (
                                <p className="text-sm text-gray-600">
                                  Correct answer: <span className="text-green-600">
                                    {question.options[question.correctAnswer]}
                                  </span>
                                </p>
                              )}
                            </>
                          ) : (
                            <>
                              <p className="text-sm text-gray-600">
                                Your answer: <span className={score >= 0.8 ? 'text-green-600 font-medium' : score > 0 ? 'text-yellow-600' : 'text-red-600'}>
                                  {userAnswer || '(No answer)'}
                                </span>
                              </p>
                              <p className="text-sm text-gray-600 mt-2 p-2 bg-green-50 rounded border border-green-100">
                                <span className="font-semibold text-green-800">Ideal Answer:</span><br/>
                                <span className="text-green-700">{question.correctAnswer}</span>
                              </p>
                            </>
                          )}
                          
                          {evaluation.feedback && (
                            <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-100">
                              <p className="text-xs text-blue-700">
                                <span className="font-semibold">Feedback:</span> {evaluation.feedback}
                              </p>
                            </div>
                          )}

                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-xs text-gray-600">
                              <span className="font-semibold">Explanation:</span> {question.explanation}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <button
                disabled={isEvaluating}
                onClick={() => {
                  setPracticeQuestions([]);
                  setUserAnswers({});
                  setCurrentQuestionIndex(0);
                  setShowResults(false);
                  setScores({});
                }}
                className="mt-4 w-full px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50"
              >
                Generate New Questions
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* End Chat Confirmation Modal */}
      {showEndChatModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-800 mb-4">End Learning Session?</h3>
            <p className="text-gray-600 mb-2">
              You've been studying for <span className="font-semibold text-indigo-600">{formatTime(elapsedTime)}</span>
            </p>
            <p className="text-gray-600 mb-6">
              Are you sure you want to end this session? You can export your notes as PDF before leaving.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowEndChatModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Continue Learning
              </button>
              <button
                onClick={exportToPDF}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export & End
              </button>
              <button
                onClick={confirmEndChat}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                End Session
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIEducator;
